import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Col, Container, Dropdown, Row, Form } from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Accordion } from "react-bootstrap";
import { config } from "../../config";
import Select from "react-select";
import { AppContext } from "../../AppContext/Context";
import Modal from "react-bootstrap/Modal";
import { api } from "../../API";
import Button from "react-bootstrap/Button";
import Loader from "../loader";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";
import useHandleUnauthorized from "../../hooks/useHandleUnauthorized/UseHandleUnauthorized";
import SweetLayout from "../../Layout/SweetLayout";

interface ChannelDetailsData {
  channel: number;
  name: string;
  url?: string;
  confidential?: boolean;
  company_name?: string;
  type: string;
  status: string;
  owner_account_id?: number | null;
  generate?: string;
  ksc?: {
    ksc_id: number;
    name?: string;
    kss?: number[];
    confidential?: boolean;
    [key: string]: any;
  };
  channel_details?: {
    [key: string]: any;
  };
  ept_config?: {
    [key: string]: any;
  };
}

interface KscDropdownData {
  data?: KscItem[];
}

interface KscItem {
  ksc_id: number;
  name: string;
  Ksc_id?: number;
}

interface SelectOption {
  value: string | number;
  label: string;
  key?: number;
  Ksc_id?: number;
}

interface EditableFields {
  name: boolean;
  url: boolean;
  cn: boolean;
  confidential: boolean;
  type: boolean;
  owner_account_id: boolean;
  sourceDetails: boolean;
  ksc: boolean;
  accepted_origin_list: boolean;
  other_fields: boolean;
  generate: boolean;
}

interface FormData {
  name: string;
  url: string;
  confidential: string | boolean;
  type: string;
  owner_account_id: string | number;
  channel_details: {
    [key: string]: any;
  };
  status: string;
  generate?: string;
  cn?: string;
}

interface KeyValuePair {
  key: string;
  value: string;
}

interface DropdownForTypeProps {
  newType: string;
  handleNewType: (eventKey: string) => void;
  selectedValue?: string;
}

const DropdownForType: React.FC<DropdownForTypeProps> = ({ newType, handleNewType }) => {
  return (
    <Dropdown onSelect={(eventKey) => handleNewType(eventKey || "")}>
      <Dropdown.Toggle variant="outline-success" id="dropdown-basic">
        {newType}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item eventKey="Chat">Chat</Dropdown.Item>
        <Dropdown.Item eventKey="Email">Email</Dropdown.Item>
        <Dropdown.Item eventKey="Community Forum">
          Community Forum
        </Dropdown.Item>
        <Dropdown.Item eventKey="Hubspot">Hubspot</Dropdown.Item>
        <Dropdown.Item eventKey="Zendesk">Zendesk</Dropdown.Item>
        <Dropdown.Item eventKey="Slack">Slack</Dropdown.Item>
        <Dropdown.Item eventKey="ept-ai-app">ept-ai-app</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const ChannelsDetailedTable: React.FC = () => {
  const [data, setData] = useState<ChannelDetailsData>({} as ChannelDetailsData);
  const [kscForDropdown, setKscForDropdown] = useState<KscDropdownData>({});
  const [newPayload, setNewPayload] = useState<any>();
  const [hideInputAtIndex, setHideInputAtIndex] = useState<boolean[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [editedArrays, setEditedArrays] = useState<{ [key: string]: boolean }>({});
  const [editableActive, setEditableActive] = useState<boolean>(false);
  const [editedValues, setEditedValues] = useState<{ [key: string]: any }>({});
  const [isActive, setIsActive] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [newName, setNewName] = useState<string>("");
  const [newGenerate, setNewGenerate] = useState<string>("");
  const [newUrl, setNewUrl] = useState<string>("");
  const [newConfidential, setNewConfidential] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const { id } = useParams<{ id: string }>();
  const { isLoading, getIdTokenClaims, user } = useAuth0();
  const [editMode, setEditMode] = useState<boolean>(false);
  const showEditButton = user?.ept_roles?.includes("EptAdmin");
  const { setIsLoad, isLoad } = useContext(AppContext);
  const handleUnauthorized = useHandleUnauthorized();

  const [editableFields, setEditableFields] = useState<EditableFields>({
    name: false,
    url: false,
    cn: false,
    confidential: false,
    type: false,
    owner_account_id: false,
    sourceDetails: false,
    ksc: false,
    accepted_origin_list: false,
    other_fields: false,
    generate: false,
  });

  const [formData, setFormData] = useState<FormData>({
    name: "",
    url: "",
    confidential: "",
    type: data.type || "",
    owner_account_id: "N/A",
    channel_details: {},
    status: data.status || "",
  });

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDelModal, setShowDelModal] = useState<boolean>(false);
  const nav = useNavigate();

  const showGenericDeleteModal = (): void => {
    setShowDelModal(true);
  };

  const handleCloseDelModal = (): void => {
    setShowDelModal(false);
  };

  const handleDelForChannel = async (): Promise<void> => {
    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel/${id}`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        await axios.delete(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        nav("/channel");
      } catch (error: any) {
        console.error("API request error:", error.message);
      }
    }
  };

  const handleOpenModal = (): void => {
    setShowModal(true);
  };

  const handleCloseModal = async (): Promise<void> => {
    setShowModal(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!isLoading) {
        setIsLoad(true);
        const endpoint = `${config.baseURL}/admin/v1/channel/${id}`;
        try {
          const getidToken = await getIdTokenClaims();
          const idToken = getidToken?.__raw;
          const response = await axios.get<ChannelDetailsData>(endpoint, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
          setData(response.data);
        } catch (error: any) {
          console.error("API request error:", error.message);
          handleUnauthorized(error);
        } finally {
          setIsLoad(false);
        }
      }
    };

    const fetchData2 = async () => {
      if (!isLoading) {
        setIsLoad(true);
        const endpoint = `${config.baseURL}/admin/v1/ksc`;
        try {
          const getidToken = await getIdTokenClaims();
          const idToken = getidToken?.__raw;
          const response = await axios.get<KscItem[]>(endpoint, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
          setKscForDropdown({ data: response.data });
        } catch (error: any) {
          console.error("API request error:", error.message);
          handleUnauthorized(error);
        } finally {
          setIsLoad(false);
        }
      }
    };
    fetchData2();
    fetchData();
  }, [isLoading, setIsLoad, id, getIdTokenClaims, handleUnauthorized]);

  useEffect(() => {
    if (data) {
      setNewConfidential(data.confidential || false);
      setIsActive(data.status === "active");
    }
  }, [data]);

  const handleEditClick = (fieldName: string, value?: any): void => {
    if (fieldName === "ksc") {
      setEditMode(true);
    }
    setEditableFields((prevState) => ({
      ...prevState,
      [fieldName]: true,
    }));
    setEditedValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  useEffect(() => {}, [formData]);

  const handleSaveArrayItem = (fieldName: string, index?: number): void => {
    const updatedChannelDetails = { ...data.channel_details };
    updatedChannelDetails[fieldName] = editedValues[fieldName];

    setData((prevData) => ({
      ...prevData,
      channel_details: updatedChannelDetails,
    }));

    setFormData((prevFormData) => ({
      ...prevFormData,
      channel_details: updatedChannelDetails,
    }));

    setEditedValues((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
    toggleEditedArray(fieldName);
  };

  const handleSaveKscClick = async (fieldName: string, value?: any): Promise<void> => {
    if (fieldName === "ksc") {
      setEditMode(false);
    }
    toggleEditedArray(fieldName);
    setEditableFields((prevState) => ({
      ...prevState,
      [fieldName]: false,
    }));
    handleSaveActiveClick();

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      await api.put(
        `${config.baseURL}/admin/v1/channel/${id}`,
        {
          ksc_id: selectedOption,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
    } catch (error: any) {
      console.error("Error:", error);
    }

    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel/${id}`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.get<ChannelDetailsData>(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        setData(response.data);
      } catch (error: any) {
        console.error("API request error:", error.message);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const handleSaveClick = async (fieldName: string, value?: any): Promise<void> => {
    if (fieldName === "ksc") {
      setEditMode(false);
    }
    toggleEditedArray(fieldName);
    setEditableFields((prevState) => ({
      ...prevState,
      [fieldName]: false,
      other_fields: false,
    }));
    handleSaveActiveClick();
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: editedValues[fieldName],
    }));

    setEditedValues((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));

    let updatedChannelDetails = { ...data.channel_details };
    if (updatedChannelDetails && updatedChannelDetails.hasOwnProperty(fieldName)) {
      updatedChannelDetails[fieldName] = editedValues[fieldName];
    }

    setData((prevData) => ({
      ...prevData,
      channel_details: updatedChannelDetails,
    }));

    setEditedValues((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));

    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: "",
    }));

    try {
      const getidToken = await getIdTokenClaims();
      setIsLoad(true);
      const idToken = getidToken?.__raw;
      await api.put(
        `${config.baseURL}/admin/v1/channel/${id}`,
        {
          ...formData,
          channel_details: updatedChannelDetails,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
    } catch (error: any) {
      console.error("Error:", error);
    } finally {
      setIsLoad(false);
    }

    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel/${id}`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.get<ChannelDetailsData>(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        setData(response.data);
      } catch (error: any) {
        console.error("API request error:", error.message);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const handlesave = async (fieldName: string): Promise<void> => {
    if (fieldName === "ksc") {
      setEditMode(false);
    }
    toggleEditedArray(fieldName);
    setEditableFields((prevState) => ({
      ...prevState,
      [fieldName]: false,
      other_fields: false,
    }));

    handleSaveActiveClick();
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: editedValues[fieldName],
    }));

    setEditedValues((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));

    let updatedChannelDetails = { ...data.channel_details };
    if (updatedChannelDetails && updatedChannelDetails.hasOwnProperty(fieldName)) {
      updatedChannelDetails[fieldName] = editedValues[fieldName];
    }

    setData((prevData) => ({
      ...prevData,
      channel_details: updatedChannelDetails,
    }));

    setEditedValues((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));

    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: "",
    }));

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      await api.put(
        `${config.baseURL}/admin/v1/channel/${id}`,
        {
          ...formData,
          channel_details: newPayload ? newPayload : updatedChannelDetails,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      setNewPayload(undefined);
    } catch (error: any) {
      console.error("Error:", error);
    }

    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel/${id}`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.get<ChannelDetailsData>(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        setData(response.data);
      } catch (error: any) {
        console.error("API request error:", error.message);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const handleSelectChange = (selected: SelectOption): void => {
    setSelectedOption(selected.Ksc_id || 0);
  };

  const handleNewType = (e: string): void => {
    setSelectedType(e);
  };

  const handleNewName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setNewName(e.target.value);
  };

  const handleNewGenerate = (e: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }, fieldName?: string): void => {
    setNewGenerate(e.target.value);
  };

  const handleNewConfidential = (e: React.ChangeEvent<HTMLInputElement>, currentValue?: boolean): void => {
    setNewConfidential(e.target.checked);
  };

  const handleNewUrl = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setNewUrl(e.target.value);
  };

  useEffect(() => {
    if (data) {
      setFormData({
        name: newName ? newName : data.name,
        generate: newGenerate ? newGenerate : data.generate || "",
        url: newUrl ? newUrl : data.url || "",
        confidential: newConfidential ? newConfidential : newConfidential,
        type: selectedType ? selectedType : data.type,
        status: isActive ? "active" : "not active",
        owner_account_id:
          data.owner_account_id !== undefined && data.owner_account_id !== null
            ? data.owner_account_id
            : data.owner_account_id === 0
            ? 0
            : "N/A",
        channel_details: { ...data.channel_details },
      });
    }
  }, [
    data,
    selectedValue,
    isActive,
    selectedOption,
    newUrl,
    newConfidential,
    newName,
    newGenerate,
    selectedType,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string, nestedField: string | null = null, index: number | null = null): void => {
    if (nestedField !== null) {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: {
          ...(prevState[fieldName as keyof FormData] as any),
          [nestedField]: e.target.value,
        },
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]:
          e.target.type === "checkbox" ? e.target.checked : e.target.value,
      }));
    }
  };

  const toggleEditedArray = (fieldName: string): void => {
    setEditedArrays((prevEditedArrays) => ({
      ...prevEditedArrays,
      [fieldName]: !prevEditedArrays[fieldName],
    }));
  };

  const handleEditArray = (fieldName: string): void => {
    if (data.channel_details) {
      setEditedValues((prev) => ({
        ...prev,
        [fieldName]: [...data.channel_details![fieldName]],
      }));
    }
    toggleEditedArray(fieldName);
    if (fieldName === "accepted_origin_list") {
      setEditableFields((prev) => ({
        ...prev,
        accepted_origin_list: true,
      }));
    } else {
      setEditableFields((prev) => ({
        ...prev,
        other_fields: true,
      }));
    }
  };

  const handleEditArrayItemChange = (fieldName: string, index: number, e: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedArray = [...editedValues[fieldName]];
    updatedArray[index] = e.target.value;
    setEditedValues((prev) => ({
      ...prev,
      [fieldName]: updatedArray,
    }));
  };

  const handleAddOriginItem = (): void => {
    const updatedData = { ...data };
    if (updatedData.channel_details?.accepted_origin_list) {
      updatedData.channel_details.accepted_origin_list.push("");
    }
    setData(updatedData);
  };

  const handleDeleteArrayItem = async (key: string, index: number): Promise<void> => {
    let updatedChannelDetails: any;
    const confirmed = window.confirm("Do you want to delete this?");
    if (confirmed) {
      setData((prevData) => {
        updatedChannelDetails = { ...prevData.channel_details };
        if (Array.isArray(updatedChannelDetails[key])) {
          updatedChannelDetails[key] = updatedChannelDetails[key].filter(
            (_: any, i: number) => i !== index
          );
        } else {
          delete updatedChannelDetails[key];
        }
        return {
          ...prevData,
          channel_details: updatedChannelDetails,
        };
      });
      if (key === "accepted_origin_list") {
        setEditableFields((prev) => ({
          ...prev,
          accepted_origin_list: false,
        }));
      } else {
        setEditableFields((prev) => ({
          ...prev,
          other_fields: false,
        }));
      }
      setEditedValues((prev) => ({
        ...prev,
        [key]: undefined,
      }));
    }
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      setNewPayload(updatedChannelDetails);
      await api.put(
        `${config.baseURL}/admin/v1/channel/${id}`,
        {
          ...formData,
          channel_details: updatedChannelDetails,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      setNewPayload(undefined);
    } catch (error: any) {
      console.error("Error:", error);
    }

    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel/${id}`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.get<ChannelDetailsData>(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        setData(response.data);
      } catch (error: any) {
        console.error("API request error:", error.message);
      } finally {
        setIsLoad(false);
      }
    }
  };

  let keyIndex = 0;

  const [newKeyValuePairs, setNewKeyValuePairs] = useState<KeyValuePair[]>([]);

  const handleAddKeyValuePair = (): void => {
    setNewKeyValuePairs((prevPairs) => [...prevPairs, { key: "", value: "" }]);
  };

  const handleInputChange = (index: number, keyOrValue: "key" | "value", newValue: string): void => {
    setNewKeyValuePairs((prevPairs) => {
      const updatedPairs = [...prevPairs];
      updatedPairs[index][keyOrValue] = newValue;
      return updatedPairs;
    });
  };

  const handleSaveClick1 = async (key: string, index: number): Promise<void> => {
    const updatedFormData = { ...formData };
    const updatedChannelDetails = { ...updatedFormData.channel_details };
    const pair = newKeyValuePairs.find((pair) => pair.key === key);
    if (pair) {
      updatedChannelDetails[key] = pair.value;
      updatedFormData.channel_details = updatedChannelDetails;
      setFormData(updatedFormData);
      setHideInputAtIndex((prevHideInput) => {
        const updatedHideInput = [...prevHideInput];
        updatedHideInput[index] = true;
        return updatedHideInput;
      });

      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        await api.put(
          `${config.baseURL}/admin/v1/channel/${id}`,
          {
            ...formData,
            channel_details: updatedChannelDetails,
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
      } catch (error: any) {
        console.error("Error:", error);
      }
    } else {
      console.error(`Key '${key}' not found in newKeyValuePairs.`);
    }

    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel/${id}`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.get<ChannelDetailsData>(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        setData(response.data);
      } catch (error: any) {
        console.error("API request error:", error.message);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const handleDeleteArrayItem1 = (key: string, index: number): void => {
    const updatedPairs = [...newKeyValuePairs];
    updatedPairs.splice(index, 1);
    setNewKeyValuePairs(updatedPairs);
  };

  const renderDetail = (details: { [key: string]: any }): JSX.Element[] => {
    return Object.entries(details).map(([key, value], index) => {
      const uniqueKey = keyIndex++;

      if (Array.isArray(value)) {
        const isEdited = editedArrays[key] || false;
        if (key === "accepted_origin_list") {
          return (
            <div key={uniqueKey}>
              <strong>{key}: </strong>
              {value.map((item, index) => (
                <div key={index}>
                  <input
                    readOnly={!editableFields.accepted_origin_list}
                    type="text"
                    style={{
                      border: editableFields.accepted_origin_list
                        ? "1px solid #ccc"
                        : "none",
                      outline: "none",
                    }}
                    value={editedValues[key] ? editedValues[key][index] : item}
                    onChange={(e) => handleEditArrayItemChange(key, index, e)}
                  />
                  {editedValues[key] !== undefined && (
                    <i
                      className="fa fa-trash"
                      onClick={() => {
                        handleDeleteArrayItem(key, index);
                      }}
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                    ></i>
                  )}
                </div>
              ))}

              {editedValues[key] === undefined && showEditButton && (
                <>
                  <i
                    className="fa fa-pen-to-square"
                    onClick={() => handleEditArray(key)}
                    style={{ cursor: "pointer", marginLeft: "5px" }}
                  ></i>
                </>
              )}
              {editedValues[key] !== undefined && (
                <>
                  <i
                    className="fa fa-save"
                    onClick={() => handlesave(key)}
                    style={{ cursor: "pointer", marginLeft: "5px" }}
                  ></i>
                </>
              )}
            </div>
          );
        } else {
          return (
            <div key={uniqueKey}>
              <strong>{key}: </strong>
              {value.map((item, index) => (
                <div key={index}>
                  <input
                    readOnly={!editableFields.other_fields}
                    type="text"
                    style={{
                      border: isEdited ? "1px solid #ccc" : "none",
                      outline: "none",
                    }}
                    value={editedValues[key] ? editedValues[key][index] : item}
                    onChange={(e) => handleEditArrayItemChange(key, index, e)}
                  />
                </div>
              ))}
              {editedValues[key] === undefined && showEditButton && (
                <i
                  className="fa fa-pen-to-square"
                  onClick={() => handleEditArray(key)}
                  style={{ cursor: "pointer", marginLeft: "5px" }}
                ></i>
              )}
              {editedValues[key] !== undefined && (
                <>
                  {" "}
                  <i
                    className="fa fa-save"
                    onClick={() => handleSaveClick(key)}
                    style={{ cursor: "pointer", marginLeft: "5px" }}
                  ></i>
                  <i
                    className="fa fa-trash"
                    onClick={() => handleDeleteArrayItem(key, index)}
                    style={{ cursor: "pointer", marginLeft: "5px" }}
                  ></i>
                </>
              )}
            </div>
          );
        }
      } else if (typeof value === "object" && value !== null) {
        return (
          <div key={uniqueKey}>
            <strong>{key}: </strong>
            {renderDetail(value)}
          </div>
        );
      } else {
        return (
          <div key={uniqueKey}>
            <strong>{key}: </strong>
            {value}

            {editedValues[key] !== undefined ? (
              <>
                <div className="d-flex justify-content-between">
                  <div className="w-100">
                    <input
                      className="w-100"
                      type="text"
                      value={editedValues[key]}
                      onChange={(e) => handleEditClick(key, e.target.value)}
                    />
                  </div>
                  <div className="d-flex align-items-center">
                    <i
                      className="fa fa-save"
                      onClick={() => handleSaveClick(key)}
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                    ></i>
                    <i
                      className="fa fa-trash"
                      onClick={() => handleDeleteArrayItem(key, index)}
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                    ></i>
                  </div>
                </div>
              </>
            ) : (
              showEditButton && (
                <i
                  className="fa fa-pen-to-square"
                  onClick={() => handleEditClick(key, value)}
                  style={{ cursor: "pointer", marginLeft: "5px" }}
                ></i>
              )
            )}
          </div>
        );
      }
    });
  };

  const renderDetails = (details: { [key: string]: any }): JSX.Element | null => {
    if (!details || typeof details !== "object") {
      console.error("Invalid details:", details);
      return null;
    }

    const renderValue = (key: string, value: any): JSX.Element => {
      if (Array.isArray(value)) {
        return (
          <ul key={key}>
            <strong>{key}: </strong>
            {value.map((item, index) => (
              <li key={index}>
                {typeof item === "object" && item !== null ? (
                  renderDetails(item)
                ) : (
                  <input
                    readOnly
                    type="text"
                    style={{
                      border: "none",
                      outline: "none",
                    }}
                    value={item !== null && item !== undefined ? item : "null"}
                  />
                )}
              </li>
            ))}
          </ul>
        );
      } else if (typeof value === "object" && value !== null) {
        return (
          <ul key={key}>
            <strong>{key}: </strong>
            {renderDetails(value)}
          </ul>
        );
      } else {
        return (
          <li key={key}>
            <strong>{key}: </strong>
            {value !== null && value !== undefined ? value : ""}
          </li>
        );
      }
    };

    return (
      <ul>
        {Object.entries(details).map(([key, value]) => (
          <li key={key}>{renderValue(key, value)}</li>
        ))}
      </ul>
    );
  };

  const details =
    data && data.channel_details ? renderDetails(data.channel_details) : null;
  const detail =
    data && data.channel_details ? renderDetail(data.channel_details) : null;

  const handleEditActiveClick = (): void => {
    setEditableActive(true);
  };

  const handleSaveActiveClick = (): void => {
    setEditableActive(false);
  };

  const handleCheckboxChange = (): void => {
    setIsActive((prevIsActive) => !prevIsActive);
  };

  const ept_config =
    data && data?.ept_config
      ? Object.entries(data?.ept_config).map(([key, value]) => (
          <div key={key}>
            <strong>{key}: </strong>
            {value}
          </div>
        ))
      : null;

  const ksc_details = (
    <ul>
      {data && data?.ksc
        ? Object.entries(data?.ksc).map(([key, value]) => (
            <li key={key}>
              <div className="mt-3">
                <strong>{key}: </strong>
                {Array.isArray(value) && key === "kss" ? (
                  value.join(", ")
                ) : key === "confidential" ? (
                  String(value)
                ) : key === "ksc_id" ? (
                  <Link to={`/ksc-details/${value}`}>{value}</Link>
                ) : (
                  String(value)
                )}
              </div>
            </li>
          ))
        : null}
    </ul>
  );

  function channelDetailsModal(): JSX.Element {
    return (
      <Modal
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        show={showModal}
        onHide={handleCloseModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Edit Channel Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Modal.Body>
            <div style={{ display: "flex", justifyContent: "right" }}>
              <i
                className="fa fa-plus-square-o"
                aria-hidden="true"
                onClick={handleAddKeyValuePair}
                style={{ cursor: "pointer", marginLeft: "5px" }}
              ></i>
            </div>

            {detail}
            {newKeyValuePairs.map((pair, index) => (
              <div key={index}>
                {!hideInputAtIndex[index] && (
                  <>
                    <input
                      type="text"
                      value={pair.key}
                      onChange={(e) =>
                        handleInputChange(index, "key", e.target.value)
                      }
                      placeholder="Enter key"
                    />
                    :
                    <input
                      type="text"
                      value={pair.value}
                      onChange={(e) =>
                        handleInputChange(index, "value", e.target.value)
                      }
                      placeholder="Enter value"
                    />
                    {editedValues[pair.key] === undefined && (
                      <>
                        <i
                          className="fa fa-save"
                          onClick={() => handleSaveClick1(pair.key, index)}
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                        ></i>
                        <i
                          className="fa fa-trash"
                          onClick={() =>
                            handleDeleteArrayItem1(pair.key, index)
                          }
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                        ></i>
                      </>
                    )}
                  </>
                )}
              </div>
            ))}
          </Modal.Body>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <SweetLayout>
      <Container fluid>
        <div>
          {isLoad && (
            <div
              style={{
                position: "absolute",
                left: "60%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                width: "100%",
              }}
            >
              <Loader />
            </div>
          )}
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  onClick={() => nav(-1)}
                  className="fe-5 fw-bold"
                  style={{ cursor: "pointer" }}
                >
                  <span className="bi bi-chevron-left"></span> Back
                </div>
                <div>
                  <h1>Channel</h1>
                </div>
                <div>
                  <h2>Channel ID: {data.channel}</h2>
                </div>
              </div>
              <div className="p-3">
                <Row className="d-flex aligns-item-center">
                  <Col sm="12" md="12" lg="3">
                    <div>
                      <span
                        className={`custom-badge ${
                          data.status === "active" ? "active" : "inactive"
                        }`}
                      >
                        {editableActive ? (
                          <input
                            type="checkbox"
                            readOnly={!editableActive}
                            checked={isActive}
                            onChange={handleCheckboxChange}
                          />
                        ) : data.status === "active" ? (
                          "Active"
                        ) : (
                          "Not Active"
                        )}
                      </span>

                      {showEditButton && !editableActive && (
                        <i
                          className="fa fa-pen-to-square"
                          onClick={handleEditActiveClick}
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                        ></i>
                      )}
                      {editableActive && (
                        <i
                          className="fa fa-save"
                          onClick={() => handleSaveClick("status")}
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                        ></i>
                      )}
                    </div>
                  </Col>
                  <Col sm="12" md="12" lg="9">
                    <Row className="d-flex justify-content-end aligns-item-center">
                      <Col xl="3" lg="3" md="6" sm="12">
                        <p className="">
                          <strong className="me-2">Type:</strong>
                          <div className="d-flex align-items-center">
                            {editableFields?.type ? (
                              <DropdownForType
                                selectedValue={selectedValue}
                                newType={selectedType}
                                handleNewType={handleNewType}
                              />
                            ) : (
                              formData.type
                            )}
                          </div>
                        </p>
                      </Col>
                      <Col lg="3" xl="3" md="6" sm="12">
                        <p className="">
                          <strong>Company Name:</strong>
                          <div>
                            {editableFields?.cn ? (
                              <input
                                type="text"
                                defaultValue={formData.cn}
                                className="white-background-input"
                                style={{
                                  border: editableFields.cn
                                    ? "1px solid #ccc"
                                    : "none",
                                  outline: "none",
                                }}
                                onChange={(e) => handleChange(e, "cn")}
                              />
                            ) : (
                              data.company_name
                            )}
                          </div>
                        </p>
                      </Col>
                      <Col xl="3" lg="3" md="6" sm="12">
                        <p className="">
                          <div className="">
                            <strong>Owner Account ID:</strong>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            {editableFields?.owner_account_id ? (
                              <input
                                type="text"
                                defaultValue={data.owner_account_id || "N/A"}
                                className="white-background-input"
                                style={{
                                  border: editableFields.owner_account_id
                                    ? "1px solid #ccc"
                                    : "none",
                                  outline: "none",
                                }}
                                onChange={(e) =>
                                  handleChange(e, "owner_account_id")
                                }
                              />
                            ) : (
                              formData.owner_account_id
                            )}
                            {showEditButton &&
                              !editableFields.owner_account_id && (
                                <i
                                  className="fa fa-pen-to-square"
                                  onClick={() =>
                                    handleEditClick("owner_account_id")
                                  }
                                  style={{
                                    cursor: "pointer",
                                    marginLeft: "5px",
                                  }}
                                ></i>
                              )}
                            {editableFields.owner_account_id && (
                              <i
                                className="fa fa-save"
                                onClick={() =>
                                  handleSaveClick("owner_account_id")
                                }
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "5px",
                                }}
                              ></i>
                            )}
                          </div>
                        </p>
                      </Col>
                      <Col xl="3" lg="3" md="6" sm="12">
                        <div className="">
                          <span className="fw-bold">Generate:</span>
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{ width: "100%" }}>
                              {editableFields.generate ? (
                                <div>
                                  <Select
                                    id="generate"
                                    defaultValue={{
                                      value: data?.generate,
                                      label: data?.generate,
                                    }}
                                    options={[
                                      { value: "all", label: "All" },
                                      { value: "never", label: "Never" },
                                      {
                                        value: "unresponded",
                                        label: "Unresponded",
                                      },
                                      { value: "unsolved", label: "Unsolved" },
                                    ]}
                                    onChange={(selectedOption) =>
                                      handleNewGenerate(
                                        {
                                          target: { value: selectedOption?.value as string || "" },
                                        },
                                        "generate"
                                      )
                                    }
                                  />
                                </div>
                              ) : (
                                <span>{formData.generate}</span>
                              )}
                            </span>
                            {editableFields.generate ? (
                              <span
                                className="fa fa-save"
                                onClick={() => handleSaveClick("generate")}
                                style={{ cursor: "pointer", marginLeft: "5px" }}
                              ></span>
                            ) : (
                              showEditButton && (
                                <span
                                  className="fa fa-pen-to-square"
                                  onClick={() => handleEditClick("generate")}
                                  style={{ cursor: "pointer", marginLeft: "5px" }}
                                ></span>
                              )
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>

              <div className="p-3 bg-white">
                <Row>
                  <Col md="12">
                    <h4 style={{ marginTop: 0 }}>
                      <strong>Data</strong>
                    </h4>
                  </Col>
                </Row>
                <Row>
                  <Col lg="5" md="12" sm="12" className="mt-1">
                    <strong>Name:</strong>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        readOnly={!editableFields.name}
                        defaultValue={formData.name}
                        className="white-background-input w-100"
                        style={{
                          border: editableFields.name ? "1px solid #ccc" : "none",
                          outline: "none",
                        }}
                        onChange={(e) =>
                          editableFields.name && handleNewName(e)
                        }
                      />
                      {editableFields.name ? (
                        <React.Fragment>
                          <i
                            className="fa fa-save"
                            onClick={() => handleSaveClick("name")}
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                          ></i>
                        </React.Fragment>
                      ) : (
                        showEditButton && (
                          <i
                            className="fa fa-pen-to-square"
                            onClick={() => handleEditClick("name")}
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                          ></i>
                        )
                      )}
                    </div>
                  </Col>
                  <Col lg="3" md="12" sm="12" className="mt-1">
                    <strong>Url:</strong>
                    {data.url ? (
                      <div className="d-flex justify-content-between align-items-center">
                        <input
                          type="text"
                          readOnly={!editableFields.url}
                          defaultValue={data.url}
                          className="white-background-input w-100"
                          style={{
                            border: editableFields.url
                              ? "1px solid #ccc"
                              : "none",
                            outline: "none",
                          }}
                          onChange={(e) =>
                            editableFields.url && handleNewUrl(e)
                          }
                        />
                        {editableFields.url ? (
                          <React.Fragment>
                            <i
                              className="fa fa-save"
                              onClick={() => handleSaveClick("url")}
                              style={{
                                cursor: "pointer",
                                marginLeft: "5px",
                              }}
                            ></i>
                          </React.Fragment>
                        ) : (
                          showEditButton && (
                            <i
                              className="fa fa-pen-to-square"
                              onClick={() => handleEditClick("url")}
                              style={{
                                cursor: "pointer",
                                marginLeft: "5px",
                              }}
                            ></i>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="no-url-message">This id has no URL</div>
                    )}
                  </Col>
                  <Col lg="2" md="12" sm="12" className="mt-1">
                    <strong>Confidential:</strong>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <input
                        type="checkbox"
                        disabled={!editableFields.confidential}
                        checked={newConfidential}
                        className="white-background-input"
                        style={{
                          border: editableFields.confidential
                            ? "1px solid #ccc"
                            : "none",
                          outline: "none",
                        }}
                        onChange={(e) =>
                          editableFields.confidential &&
                          handleNewConfidential(e)
                        }
                      />
                      {editableFields.confidential ? (
                        <React.Fragment>
                          <i
                            className="fa fa-save"
                            onClick={() => handleSaveClick("confidential")}
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                          ></i>
                        </React.Fragment>
                      ) : (
                        showEditButton && (
                          <i
                            className="fa fa-pen-to-square"
                            onClick={() => handleEditClick("confidential")}
                            style={{ cursor: "pointer", marginLeft: "5px" }}
                          ></i>
                        )
                      )}
                    </div>
                  </Col>
                  <Col lg="2" md="12" sm="12" className="mt-1">
                    <strong>Channel ID:</strong>{" "}
                    <div className="d-flex justify-content-between align-items-center">
                      <div>{data.channel}</div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    {" "}
                    <strong> Channel Details </strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div style={{ display: "flex", justifyContent: "right" }}>
                      {showEditButton && (
                        <i
                          className="fa fa-pen-to-square"
                          onClick={handleOpenModal}
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                        ></i>
                      )}
                    </div>
                    {channelDetailsModal()}
                    {details}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    {" "}
                    <strong> KSC </strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div style={{ maxWidth: "250px", maxHeight: "100px" }}>
                      {editMode && (
                        <Select<SelectOption>
                          options={kscForDropdown.data?.map((record, index) => ({
                            label: `${record.name}(${record.ksc_id})`,
                            value: record.ksc_id,
                            Ksc_id: record.ksc_id,
                          }))}
                          onChange={(selected) => {
                            if (selected) {
                              handleSelectChange(selected as SelectOption);
                            }
                          }}
                          value={
                            selectedOption
                              ? {
                                  label: kscForDropdown.data?.find((opt) => opt.ksc_id === selectedOption)?.name || "",
                                  value: selectedOption,
                                  Ksc_id: selectedOption,
                                }
                              : undefined
                          }
                        />
                      )}

                      {showEditButton && !editableFields.ksc && (
                        <i
                          className="fa fa-pen-to-square"
                          onClick={() => handleEditClick("ksc")}
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                        ></i>
                      )}
                      {editableFields.ksc && (
                        <i
                          className="fa fa-save"
                          onClick={() => handleSaveKscClick("ksc")}
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                        ></i>
                      )}
                    </div>

                    {ksc_details}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              {showEditButton && (
                <div className="d-flex justify-content-end mt-2">
                  <Button variant="danger" onClick={showGenericDeleteModal}>
                    Delete Channel
                  </Button>
                  <DeleteConfirmationModal
                    show={showDelModal}
                    handleClose={handleCloseDelModal}
                    handleDelete={handleDelForChannel}
                    itemName={formData.name}
                    id={id}
                  />
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Container>
    </SweetLayout>
  );
};

export default ChannelsDetailedTable;

